import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Box, IconButton, TextField, Typography } from '@mui/material'
import React from 'react'

import { getSundayBeforeMonthStart } from '../../utils/calendarHelpers'

interface CalendarControlsProps {
  selectedFromDate: string
  selectedRecurAmount: number
  onFromDateChange: (e: any) => void
  onAmountChange: (amount: number) => void
}

/**
 * CalendarControls - Presentation component for calendar date controls
 *
 * Handles the UI for selecting month for the calendar view.
 * Display period is fixed at 35 days starting from the Sunday before the first of the month.
 */
export default function CalendarControls({
  selectedFromDate,
  selectedRecurAmount,
  onFromDateChange,
  onAmountChange
}: CalendarControlsProps) {
  // Set to 35 days on mount if not already set
  React.useEffect(() => {
    if (selectedRecurAmount !== 35) {
      onAmountChange(35)
    }
  }, [selectedRecurAmount, onAmountChange])

  // Get the current month/year from selectedFromDate
  const getCurrentMonthYear = () => {
    const date = new Date(selectedFromDate)
    // Find the first day of the month that this Sunday represents
    // by adding days until we hit the 1st of a month
    for (let i = 0; i < 35; i++) {
      const checkDate = new Date(date)
      checkDate.setDate(checkDate.getDate() + i)
      if (checkDate.getDate() === 1) {
        return `${checkDate.getFullYear()}-${String(
          checkDate.getMonth() + 1
        ).padStart(2, '0')}`
      }
    }
    // Fallback to the date's own month
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      '0'
    )}`
  }

  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const [year, month] = e.target.value.split('-').map(Number)
    if (!year || !month) return
    // Create first day of selected month
    const firstOfMonth = new Date(year, month - 1, 1)
    // Get the Sunday before or on the first of the month
    const sunday = getSundayBeforeMonthStart(firstOfMonth)
    // Format date without timezone conversion
    const newDateString = `${sunday.getFullYear()}-${String(
      sunday.getMonth() + 1
    ).padStart(2, '0')}-${String(sunday.getDate()).padStart(2, '0')}`
    onFromDateChange({ currentTarget: { value: newDateString } })
  }

  const handlePreviousMonth = () => {
    const currentDate = new Date(selectedFromDate)
    // Go back one month from the first day we can see
    const firstVisibleDay = new Date(currentDate)
    firstVisibleDay.setDate(firstVisibleDay.getDate() + 7) // Move into the month
    firstVisibleDay.setMonth(firstVisibleDay.getMonth() - 1)
    const sunday = getSundayBeforeMonthStart(firstVisibleDay)
    // Format date without timezone conversion
    const newDateString = `${sunday.getFullYear()}-${String(
      sunday.getMonth() + 1
    ).padStart(2, '0')}-${String(sunday.getDate()).padStart(2, '0')}`
    onFromDateChange({ currentTarget: { value: newDateString } })
  }

  const handleNextMonth = () => {
    const currentDate = new Date(selectedFromDate)
    // Go forward one month from the first day we can see
    const firstVisibleDay = new Date(currentDate)
    firstVisibleDay.setDate(firstVisibleDay.getDate() + 7) // Move into the month
    firstVisibleDay.setMonth(firstVisibleDay.getMonth() + 1)
    const sunday = getSundayBeforeMonthStart(firstVisibleDay)
    // Format date without timezone conversion
    const newDateString = `${sunday.getFullYear()}-${String(
      sunday.getMonth() + 1
    ).padStart(2, '0')}-${String(sunday.getDate()).padStart(2, '0')}`
    onFromDateChange({ currentTarget: { value: newDateString } })
  }

  return (
    <Box
      className='calendar-controls'
      sx={{
        display: 'flex',
        gap: { xs: 0.5, sm: 1.5 },
        alignItems: 'end',
        justifyContent: 'center',
        flexWrap: 'nowrap',
        p: { xs: 1, sm: 1.5 },
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'end' }}>
        <IconButton
          onClick={handlePreviousMonth}
          size='small'
          sx={{
            mb: 0.5,
            backgroundColor: 'action.hover',
            '&:hover': {
              backgroundColor: 'action.selected'
            }
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
      </Box>

      <Box sx={{ minWidth: { xs: 130, sm: 140 }, flexShrink: 0 }}>
        <Typography
          variant='caption'
          sx={{
            fontSize: { xs: '0.7rem', sm: '0.75rem' },
            fontWeight: 500,
            mb: 0.5,
            display: 'block',
            color: 'text.primary'
          }}
        >
          Month
        </Typography>
        <TextField
          type='month'
          size='small'
          onChange={handleMonthChange}
          value={getCurrentMonthYear()}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              py: { xs: 0.75, sm: 1 },
              px: { xs: 0.5, sm: 1 }
            }
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'end' }}>
        <IconButton
          onClick={handleNextMonth}
          size='small'
          sx={{
            mb: 0.5,
            backgroundColor: 'action.hover',
            '&:hover': {
              backgroundColor: 'action.selected'
            }
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Box>
  )
}
